import React, { useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import PropTypes from "prop-types";
import { BRANDS, MODES } from "../../../configs";
import { AppContext } from "../../../context";
import getGateways from "./utils/getGateways";
import getInitialFrom from "./utils/getInitialFrom";
import fromFieldAdapter from "./FromField.adapter";
import { InputWrapper } from "../../../components/InputWrapper";
import { Typeahead } from "../../../components/Typeahead";
import { TypeaheadListViewInput } from "../../../components/TypeaheadListViewInput";
import { ListView } from "./ListView";
import { findGatewayOrDestinationMatch } from "./utils/findGatewayOrDestinationMatch";
import { PACKAGES_FROM_FIELD_ID } from "../../../configs/const";
import { getInitialRolloverAvailableDays } from "../utils/getInitialRolloverAvailableDays";

const FromField = ({
	className,
	mode,

	error,
	isOnlyFromField,

	selectedFrom,
	previousSessionValue,

	onChange,
	resetError,
	handleFocus,
	setNextButtonDisabled,
	handleSetAvailableDates,
	setSovMobileIsFromInputEmpty,
	handleSovMobileInputSelectionToView,
	setIsInitialSovMobileInputState
}) => {
	const [useAppState] = AppContext.useAppContext();
	const [typeaheadData, setTypeaheadData] = useState([]);
	const [listViewData, setListViewData] = useState({});
	const [initialGateway, setInitialGateway] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isHasSelectedItem, setIsHasSelectedItem] = useState(true);

	useDeepCompareEffect(() => {
		getGateways(useAppState.lang, useAppState.alias, useAppState.brand)
			.then(async data => {
				const [defaultGateway, list, listView] = fromFieldAdapter(
					data,
					useAppState.brand
				);
				setTypeaheadData(list);
				setListViewData({ ...listView });
				
				if (useAppState.brand === BRANDS.SOV) {
					const options = await getInitialRolloverAvailableDays(useAppState.alias, useAppState.lang, useAppState.brand);
					handleSetAvailableDates?.(options);

					let fallbackGateway = selectedFrom?.value ?? "";
					if(!selectedFrom?.value){
						fallbackGateway = useAppState.lang === "fr" ? "YUL" : "YYZ";
					}
					const filteredGateway = findGatewayOrDestinationMatch(
						data?.gateways,
						fallbackGateway
					);
					setIsLoading(false);
					setInitialGateway(filteredGateway);
				} else {
					setInitialGateway(
						getInitialFrom(data, defaultGateway, previousSessionValue)
					);

					handleSetAvailableDates?.(data?.Options);
				}
				setIsLoading(false);
			})
			.catch(err => console.error(err));
	}, [useAppState, previousSessionValue]);

	if (useAppState.brand === BRANDS.VWQ) {
		return (
			<InputWrapper
				key={useAppState.lang}
				label={useAppState.getDictionary("from")}
				id={PACKAGES_FROM_FIELD_ID}
				brand={useAppState.brand}
				className={className}
				hasSelectedItem={!!selectedFrom}
				error={error}>
				{({ isFocused, handleOnFocus, handleOnBlur }) => (
					<Typeahead
						id={PACKAGES_FROM_FIELD_ID}
						brand={useAppState.brand}
						isFocused={isFocused}
						data={typeaheadData}
						isLoading={isLoading}
						initialSelectedItem={initialGateway?.[0] ?? initialGateway}
						selectedItem={selectedFrom}
						error={error}
						onFocus={() => {
							resetError();
							handleOnFocus();
						}}
						onChange={value => {
							resetError();
							onChange(value);
						}}
						showAllSuggestionsOnClickIcon={true}
						onInput={resetError}
						onBlur={handleOnBlur}
						environment={useAppState.reactDOM}
						labels={{
							noResults: useAppState.getDictionary("no-results-found")
						}}
					/>
				)}
			</InputWrapper>
		);
	}

	let placeHolderAndLabel = useAppState.getDictionary("from");

	if (useAppState.brand === BRANDS.SOV) {
		placeHolderAndLabel = useAppState.getDictionary("traveling-from");
	}

	return (
		<TypeaheadListViewInput
			id={PACKAGES_FROM_FIELD_ID}
			mode={mode}
			brand={useAppState.brand}
			className={className}
			label={placeHolderAndLabel}
			placeholder={useAppState.brand === BRANDS.SOV ? placeHolderAndLabel : ""}
			error={error}
			isLoading={isLoading}
			isOnlyFromField={isOnlyFromField}
			isHasSelectedItem={
				useAppState.brand === BRANDS.SOV ? isHasSelectedItem : !!selectedFrom
			}
			selectedItem={selectedFrom}
			initialItem={initialGateway}
			data={typeaheadData}
			resetError={resetError}
			onChange={onChange}
			handleFocus={handleFocus}
			setNextButtonDisabled={setNextButtonDisabled}
			handleSovMobileInputSelectionToView={handleSovMobileInputSelectionToView}
			setSovMobileIsFromInputEmpty={setSovMobileIsFromInputEmpty}
			setIsInitialSovMobileInputState={setIsInitialSovMobileInputState}
			setIsHasSelectedItem={setIsHasSelectedItem}
			reactDOM={useAppState.reactDOM}
			appRoot={useAppState.appRoot}
			labels={{
				noResults: useAppState.getDictionary("no-results-found"),
				list: useAppState.getDictionary("list")
			}}>
			<ListView
				body-scroll-lock-ignore=""
				data={listViewData ?? {}}
				selectedItem={selectedFrom}
				onChange={onChange}
			/>
		</TypeaheadListViewInput>
	);
};

FromField.propTypes = {
	className: PropTypes.string,
	mode: PropTypes.string,

	error: PropTypes.bool,
	isOnlyFromField: PropTypes.bool,

	previousSessionValue: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),
	selectedFrom: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),

	resetError: PropTypes.func,
	handleFocus: PropTypes.func,
	onChange: PropTypes.func.isRequired,
	setNextButtonDisabled: PropTypes.func,
	setSovMobileIsFromInputEmpty: PropTypes.func,
	handleSovMobileInputSelectionToView: PropTypes.func,
	setIsInitialSovMobileInputState: PropTypes.func,
	handleSetAvailableDates: PropTypes.func.isRequired
};

FromField.defaultProps = {
	className: undefined,
	mode: MODES.SEARCH,

	error: false,
	isOnlyFromField: false,

	previousSessionValue: undefined,
	selectedFrom: undefined,
	resetError: () => {},
	handleFocus: () => {},
	setNextButtonDisabled: () => {},
	setSovMobileIsFromInputEmpty: () => {},
	handleSovMobileInputSelectionToView: () => {},
	setIsInitialSovMobileInputState: () => {}
};

export default FromField;
export { FromField };
